import type {
    CampaignDto,
    CampaignsDto,
} from "@campaign/data/dto/campaign.dto";
import {
    CampaignDtoSchema,
    CampaignsDtoSchema,
} from "@campaign/data/dto/campaign.dto";
import type {
    CampaignCreate,
    CampaignCreateInputProps,
    CampaignUpdate,
} from "@campaign/domain/models/campaign.model";
import {
    Campaign,
    CampaignCreateSchema,
    Campaigns,
    CampaignUpdateSchema,
} from "@campaign/domain/models/campaign.model";
import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
// eslint-disable-next-line boundaries/dependencies
import type { CampaignModalFormValuesValidated } from "@campaign/presentation/components/campaign/campaign-modal-form.view";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import type * as z from "zod";

@injectable()
export class CampaignMapper {
    map(
        campaignDto: CampaignDto,
    ): Result<Campaign, ModelError<typeof Campaign>> {
        const campaignDtoParsed = CampaignDtoSchema.safeParse(campaignDto);

        if (!campaignDtoParsed.success) {
            console.error(campaignDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return Campaign.createWithRecovery({
            id: campaignDto.id,
            name: campaignDto.name,
            startDate: campaignDto.start_date,
            endDate: campaignDto.end_date,
            cropType: campaignDto.crop_type,
            stations: campaignDto.stations,
            notebookId: campaignDto.notebook.id,
        });
    }

    mapList(campaignsDto: CampaignsDto): Campaigns {
        const campaignsDtoParsed = CampaignsDtoSchema.safeParse(campaignsDto);

        if (!campaignsDtoParsed.success) {
            console.error(campaignsDtoParsed.error.issues);
            return Campaigns.empty();
        }
        const campaigns = campaignsDto.results
            .map((campaignDto) =>
                this.map(campaignDto).match(
                    (campaign) => campaign,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping campaigns:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
            )
            .filter((campaign): campaign is Campaign => campaign !== null);

        const pagination = campaignsDto.pagination;

        const campaignsResult = Campaigns.create({
            campaigns,
            pagination: {
                count: pagination.count,
                current: pagination.current,
                pages: pagination.pages,
                perPage: pagination.per_page,
            },
        });

        return campaignsResult.match(
            (campaignList) => campaignList,
            (error) => {
                console.error("Error creating Campaigns:", error);
                return Campaigns.empty();
            },
        );
    }

    mapFormToCreate(
        campaign: CampaignModalFormValuesValidated,
    ): Result<CampaignCreate, z.ZodError<CampaignCreate>> {
        const startDate = campaign.dateRange[0];
        const endDate = campaign.dateRange[1];

        const maybeCampaignCreate = CampaignCreateSchema.safeParse({
            name: campaign.name,
            startDate: startDate ? startDate.toFormat("dd/MM/yyyy") : "",
            endDate: endDate ? endDate.toFormat("dd/MM/yyyy") : "",
            cropTypeId: campaign.cropTypeId,
            stationIds: campaign.stationIds,
        } satisfies CampaignCreateInputProps);

        return maybeCampaignCreate.success
            ? ok(maybeCampaignCreate.data)
            : err(maybeCampaignCreate.error);
    }

    mapFormToEdit(
        id: number,
        campaign: CampaignModalFormValuesValidated,
    ): Result<CampaignUpdate, z.ZodError<CampaignUpdate>> {
        const startDate = campaign.dateRange[0];
        const endDate = campaign.dateRange[1];

        const maybeCampaignUpdate = CampaignUpdateSchema.safeParse({
            id,
            name: campaign.name,
            startDate: startDate ? startDate.toFormat("dd/MM/yyyy") : "",
            endDate: endDate ? endDate.toFormat("dd/MM/yyyy") : "",
            cropTypeId: campaign.cropTypeId,
            stationIds: campaign.stationIds,
        } satisfies CampaignUpdate);

        return maybeCampaignUpdate.success
            ? ok(maybeCampaignUpdate.data)
            : err(maybeCampaignUpdate.error);
    }
}
